/* eslint complexity: ["error", {max: 9}] -- nested optional chaining for preferences access */
import { useState } from "react";

import {
  AnalyticsFieldName,
  ProfileAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import { CalendarType } from "@/constants/Calendar";
import { useToast } from "@/hooks/useToast";
import { useAnalytics } from "@/services/Analytics";
import {
  useGetFullProfileQuery,
  useUpdateCalendarPreferencesMutation,
} from "@/store/api/user";
import { useAppUserSelector } from "@/store/reducers/appUser";

interface UseCalendarSync {
  calendarToDisconnect: CalendarType | null;
  handleCalendarChange: (
    calendarType: CalendarType,
  ) => (isEnabled: boolean) => void;
  handleConfirmDisable: () => void;
  handleDismiss: () => void;
  isDeviceCalendarEnabled: boolean;
  isGoogleCalendarEnabled: boolean;
}

const useCalendarSync = (): UseCalendarSync => {
  const { externalId, isPatient } = useAppUserSelector();
  const { showToast } = useToast();
  const { track } = useAnalytics();
  const [updateCalendarPreferences] = useUpdateCalendarPreferencesMutation();
  const { data: fullProfile } = useGetFullProfileQuery();

  const isDeviceCalendarEnabled =
    fullProfile?.preferences?.notifications?.deviceCalendarEnabled ?? false;
  const isGoogleCalendarEnabled =
    fullProfile?.preferences?.notifications?.googleCalendarEnabled ?? false;

  const [calendarToDisconnect, setCalendarToDisconnect] =
    useState<CalendarType | null>(null);

  const updatePreference = ({
    calendarType,
    isEnabled,
  }: {
    calendarType: CalendarType;
    isEnabled: boolean;
  }) => {
    if (!externalId) return;

    void updateCalendarPreferences({
      isDeviceCalendarEnabled:
        calendarType === CalendarType.Apple ? isEnabled : undefined,
      isGoogleCalendarEnabled:
        calendarType === CalendarType.Google ? isEnabled : undefined,
      isPatient,
      userExternalId: externalId,
    })
      .unwrap()
      .then(() => {
        try {
          track(ProfileAnalyticsEvent.CalendarPreferenceChanged, {
            [AnalyticsFieldName.NewValue]: isEnabled,
            calendarType,
          });
        } catch {
          // Analytics failure should not affect mutation success
        }
      })
      .catch(() => {
        showToast({
          text: "Failed to update calendar settings. Please try again.",
          variant: "error",
        });
      });
  };

  const handleCalendarChange =
    (calendarType: CalendarType) => (isEnabled: boolean) => {
      if (!isEnabled) {
        setCalendarToDisconnect(calendarType);
        return;
      }

      updatePreference({ calendarType, isEnabled: true });
    };

  const handleConfirmDisable = () => {
    if (calendarToDisconnect) {
      updatePreference({
        calendarType: calendarToDisconnect,
        isEnabled: false,
      });
    }
    setCalendarToDisconnect(null);
  };

  const handleDismiss = () => {
    setCalendarToDisconnect(null);
  };

  return {
    calendarToDisconnect,
    handleCalendarChange,
    handleConfirmDisable,
    handleDismiss,
    isDeviceCalendarEnabled,
    isGoogleCalendarEnabled,
  };
};

export default useCalendarSync;
